import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import SwapVertOutlinedIcon from "@mui/icons-material/SwapVertOutlined";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  generateUrlWithParams,
  paramParsify,
  paramStringify,
} from "utils/queryParams";

function EnhancedTableHead(props) {
  const { id, order, orderBy, onRequestSort, rows } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {rows.map((headCell, idx) => {
          const sortKey = headCell?.sortKey ?? headCell?.id;
          return (
            <TableCell
              key={`${id}-head-${headCell.id}-${idx}`}
              align={headCell.numeric ? "right" : "left"}
              sortDirection={orderBy === sortKey ? order : false}
              width={headCell?.width}
            >
              {headCell?.sorter ? (
                <TableSortLabel
                  active={Boolean(headCell?.sorter)}
                  direction={orderBy === sortKey ? order : "desc"}
                  onClick={createSortHandler(sortKey)}
                  {...(orderBy !== sortKey
                    ? { IconComponent: SwapVertOutlinedIcon }
                    : {})}
                >
                  {headCell.label}
                </TableSortLabel>
              ) : (
                headCell.label
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

export default function CustomTable({
  id,
  defaultSortByKey,
  defaultSortOrder = "desc",
  emptyImgSrc = "/images/table-empty-state-no-skeleton.svg",
  headCells,
  bodyCells,
  bodyCellKey,
  rowsPerPage = 10,
  maxHeight = "100%",
  total,
  showPagination = true,
  borderless = false,
  sx = {},
  tableContainerStyle = {},
  onRowClick = () => {},
  uniqueParams = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = paramParsify(location?.search);

  const [order, setOrder] = useState(
    queryParams?.[uniqueParams ? `${id}_sort_order` : "sort_order"] ??
      defaultSortOrder,
  );
  const [orderBy, setOrderBy] = useState(
    queryParams?.[uniqueParams ? `${id}_sort_by` : "sort_by"] ??
      defaultSortByKey,
  );
  const [page, setPage] = useState(
    Number(queryParams?.[uniqueParams ? `${id}_page` : "page"] ?? 1) - 1 || 0,
  );

  const handleRequestSort = useCallback(
    (_event, property) => {
      const isAsc = orderBy === property && order === "asc";
      const resultOrder = isAsc ? "desc" : "asc";

      setOrder(resultOrder);
      setOrderBy(property);

      navigate(
        generateUrlWithParams(
          location?.pathname,
          paramStringify({
            ...queryParams,
            [uniqueParams ? `${id}_sort_by` : "sort_by"]: property,
            [uniqueParams ? `${id}_sort_order` : "sort_order"]: resultOrder,
          }),
        ),
      );
    },
    [
      location?.pathname,
      navigate,
      order,
      orderBy,
      queryParams,
      id,
      uniqueParams,
    ],
  );

  const handleChangePage = useCallback(
    (_event, newPage) => {
      setPage(newPage);
      const url = generateUrlWithParams(
        location?.pathname,
        paramStringify({
          ...queryParams,
          [uniqueParams ? `${id}_page` : "page"]: newPage + 1 || undefined,
        }),
      );
      navigate(url);
    },
    [location, navigate, queryParams, id, uniqueParams],
  );

  useEffect(() => {
    if (queryParams?.[uniqueParams ? `${id}_page` : "page"]) {
      setPage(Number(queryParams?.page ?? 1) - 1 || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(queryParams?.[uniqueParams ? `${id}_page` : "page"]),
    id,
    uniqueParams,
  ]);

  return (
    <Box sx={{ width: "100%", border: "none" }}>
      {borderless ? (
        <>
          <TableContainer style={{ maxHeight, ...tableContainerStyle }}>
            <Table
              sx={{
                width: "100%",
                ...sx,
              }}
              aria-labelledby='tableTitle'
              size='medium'
              stickyHeader
            >
              <EnhancedTableHead
                id={id}
                rows={headCells}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={bodyCells?.length ?? 0}
              />
              <TableBody>
                {bodyCells?.map((row, idx) => {
                  return (
                    <>
                      {/* eslint-disable-next-line eqeqeq */}
                      {idx == bodyCells.length - 1 && row?.isFooter && (
                        <tr>
                          <td colSpan={headCells.length}>
                            <Box
                              sx={{
                                height: "1px",
                                backgroundColor: "#B5BFC4",
                                marginTop: "8px",
                                marginBottom: "8px",
                              }}
                            />
                          </td>
                        </tr>
                      )}
                      <TableRow
                        className={row?.isFooter ? "!border-t" : ""}
                        tabIndex={-1}
                        key={`${id}-body-${
                          row?.[bodyCellKey] ?? row?.id
                        }-${idx}`}
                      >
                        {headCells.map((headCell, index) => (
                          <TableCell
                            align={headCell?.numeric ? "right" : "left"}
                            key={`${id}-body-cell-${
                              row?.[bodyCellKey] ?? row?.id
                            }-${idx}-${index}`}
                          >
                            {row?.[headCell.id]}
                          </TableCell>
                        ))}
                      </TableRow>
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {showPagination && (
            <TablePagination
              component='div'
              count={total ?? bodyCells?.length ?? 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
            />
          )}
        </>
      ) : (
        <Paper sx={{ width: "100%", mb: 2 }}>
          <TableContainer style={{ maxHeight }}>
            <Table
              sx={{ width: "100%", ...sx }}
              aria-labelledby='tableTitle'
              size='medium'
            >
              <EnhancedTableHead
                rows={headCells}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={bodyCells?.length ?? 0}
              />
              <TableBody>
                {Boolean(bodyCells?.length) ? (
                  bodyCells?.map((row, idx) => {
                    return (
                      <TableRow
                        tabIndex={-1}
                        key={`${id}-body-${
                          row?.[bodyCellKey] ?? row?.id
                        }-${idx}`}
                        onClick={() => onRowClick(row)}
                      >
                        {headCells.map((headCell, index) => (
                          <TableCell
                            align={headCell?.numeric ? "right" : "left"}
                            key={`${id}-body-cell-${
                              row?.[bodyCellKey] ?? row?.id
                            }-${idx}-${index}`}
                          >
                            {row?.[headCell.id]}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableCell
                    className='my-5'
                    align='center'
                    colSpan={headCells?.length}
                  >
                    <img
                      alt='table-empty-icon'
                      src={emptyImgSrc}
                      className='m-auto'
                    />
                  </TableCell>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {showPagination && (
            <TablePagination
              component='div'
              count={total ?? bodyCells?.length ?? 0}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[]}
              page={page}
              onPageChange={handleChangePage}
            />
          )}
        </Paper>
      )}
    </Box>
  );
}
